import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmDialog from '../components/ui/ConfirmDialog';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' usa estilo vermelho (ações destrutivas) — o padrão é neutro. */
  variant?: 'danger' | 'default';
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

/**
 * Provider global de confirmação — substitui window.confirm() por um modal
 * consistente com a marca, com Promise<boolean> no mesmo espírito da API nativa
 * (await confirm({...}) resolve true/false conforme a escolha do usuário).
 *
 * Desenha com o ConfirmDialog, o mesmo modal das confirmações por estado,
 * e serve para qualquer confirmação, destrutiva ou não, em qualquer tela.
 */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts);
    return new Promise<boolean>(resolve => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleClose = (result: boolean) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setOptions(null);
  };

  const isDanger = options?.variant !== 'default' && options?.variant !== undefined
    ? options.variant === 'danger'
    : true; // destrutivo é o caso mais comum de confirm(); default seguro é 'danger'

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        isOpen={!!options}
        onClose={() => handleClose(false)}
        onConfirm={() => handleClose(true)}
        title={options?.title || (isDanger ? 'Confirmar exclusão' : 'Confirmar ação')}
        message={options?.message ?? ''}
        confirmLabel={options?.confirmLabel}
        cancelLabel={options?.cancelLabel}
        variant={isDanger ? 'danger' : 'default'}
      />
    </ConfirmContext.Provider>
  );
};

/**
 * Hook para pedir confirmação de qualquer componente dentro do <ConfirmProvider>.
 * Uso: const { confirm } = useConfirm();
 *      if (await confirm({ message: 'Tem certeza?' })) { ... }
 * Substitui window.confirm() nativo (bloqueante, sem identidade visual).
 */
export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm deve ser usado dentro de um <ConfirmProvider>');
  }
  return ctx;
}
